import React from 'react';
import { Lightbulb, TrendingUp, DollarSign, Zap } from 'lucide-react';

/**
 * Smart Suggestions Component
 * Provides intelligent recommendations based on service and data entered
 */
export default function SmartSuggestions({ 
  serviceId, 
  volumeAssumptions,
  proposedPricing,
  visible = true 
}) {
  if (!visible) return null;

  const suggestions = [];

  // Service-specific suggestions
  const serviceSuggestions = {
    'child-support': {
      icon: DollarSign,
      color: 'blue',
      tips: [
        'Child Support typically has 14-20% ACH volume',
        'Average transaction: $200-$400',
        'Recommended GPM target: 55-65%',
        'Min fees: $0.95-$1.50 per transaction'
      ]
    },
    'traffic': {
      icon: TrendingUp,
      color: 'green',
      tips: [
        'Traffic payments are often lower value ($50-$150)',
        'Higher CC/Debit mix (70-80% combined)',
        'Competitive pricing needed (2.0-2.5% take rate)',
        'High volume justifies lower margins'
      ]
    },
    'utilities': {
      icon: Zap,
      color: 'purple',
      tips: [
        'Utilities have high transaction values ($100-$500)',
        'Consistent monthly payment patterns',
        'ACH preferred (25-35% mix)',
        'Target GPM: 50-60%'
      ]
    }
  };

  // Volume-based suggestions
  if (volumeAssumptions.totalTransactions > 100000) {
    suggestions.push({
      title: 'High Volume Detected',
      message: 'With over 100k transactions, consider volume-based pricing discounts',
      type: 'optimization',
      color: 'indigo'
    });
  }

  if (volumeAssumptions.achPercent > 0 && volumeAssumptions.achPercent < 10) {
    suggestions.push({
      title: 'Low ACH Volume',
      message: 'ACH has highest margins (~80%). Consider promoting ACH to increase profitability',
      type: 'revenue',
      color: 'green'
    });
  }

  // Pricing suggestions
  if (proposedPricing.paymentRates?.CC?.takeRate > 0) {
    const ccRate = proposedPricing.paymentRates.CC.takeRate;
    if (ccRate > 3.0) {
      suggestions.push({
        title: 'Pricing Competitiveness',
        message: `CC rate of ${ccRate}% may be high. Industry average: 2.25-2.75%`,
        type: 'competitive',
        color: 'amber'
      });
    } else if (ccRate < 2.0) {
      suggestions.push({
        title: 'Margin Alert',
        message: `CC rate of ${ccRate}% is very competitive but verify merchant fees don't exceed this`,
        type: 'warning',
        color: 'red'
      });
    }
  }

  const getServiceSuggestion = () => {
    const normalizedServiceId = serviceId?.toLowerCase() || '';
    for (const [key, value] of Object.entries(serviceSuggestions)) {
      if (normalizedServiceId.includes(key)) {
        return value;
      }
    }
    return null;
  };

  const serviceSuggestion = getServiceSuggestion();

  if (!serviceSuggestion && suggestions.length === 0) return null;

  return (
    <div className="space-y-3">
      {/* Service-specific suggestions */}
      {serviceSuggestion && (
        <div className={`bg-gradient-to-br from-${serviceSuggestion.color}-50 to-${serviceSuggestion.color}-100 border-2 border-${serviceSuggestion.color}-200 rounded-lg p-4`}>
          <div className="flex items-start gap-3">
            <div className={`w-10 h-10 rounded-full bg-${serviceSuggestion.color}-600 flex items-center justify-center flex-shrink-0`}>
              <serviceSuggestion.icon className="w-5 h-5 text-white" />
            </div>
            <div className="flex-1">
              <h4 className={`text-sm font-semibold text-${serviceSuggestion.color}-900 mb-2`}>
                Industry Insights for This Service
              </h4>
              <ul className="space-y-1">
                {serviceSuggestion.tips.map((tip, idx) => (
                  <li key={idx} className={`text-xs text-${serviceSuggestion.color}-800 flex items-start gap-2`}>
                    <span className="text-xs">•</span>
                    <span>{tip}</span>
                  </li>
                ))}
              </ul>
            </div>
          </div>
        </div>
      )}

      {/* Dynamic suggestions */}
      {suggestions.map((suggestion, idx) => {
        const colorClasses = {
          indigo: 'bg-indigo-50 border-indigo-300 text-indigo-900',
          green: 'bg-green-50 border-green-300 text-green-900',
          amber: 'bg-amber-50 border-amber-300 text-amber-900',
          red: 'bg-red-50 border-red-300 text-red-900'
        };

        return (
          <div 
            key={idx} 
            className={`${colorClasses[suggestion.color]} border-l-4 rounded-r-lg p-3 shadow-sm`}
          >
            <div className="flex items-start gap-2">
              <Lightbulb className="w-4 h-4 flex-shrink-0 mt-0.5" />
              <div>
                <h5 className="text-sm font-semibold mb-1">{suggestion.title}</h5>
                <p className="text-xs opacity-90">{suggestion.message}</p>
              </div>
            </div>
          </div>
        );
      })}
    </div>
  );
}
